import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Field } from 'react-final-form';
import { payPalClientId } from 'config';
import { PayPalScriptProvider, PayPalCardFieldsProvider } from '@paypal/react-paypal-js';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { errorNotification } from 'utils';
import { requestInProcess } from 'selectors/request';
import { createOrder, onApprove } from 'actions/appraisalPayment';
import { FormFlex, FormBox, SubmitError, FormLegend } from 'components/shared/form';
import Fields from './Fields';
import Loader from './Loader';
import SubmitPayment from './PayButton';

const FIELD_NAME_MAP = {
  cardNameField: 'appraisalPayment.creditCard.nameOnCard',
  cardNumberField: 'appraisalPayment.creditCard.number',
  cardExpiryField: 'appraisalPayment.creditCard.expiryDate',
  cardCvvField: 'appraisalPayment.creditCard.verificationValue',
  name: 'appraisalPayment.creditCard.nameOnCard',
  number: 'appraisalPayment.creditCard.number',
  expiry: 'appraisalPayment.creditCard.expiryDate',
  cvv: 'appraisalPayment.creditCard.verificationValue',
};

const handleCardFieldChange = (event, form) => {
  const { fields } = event;
  Object.entries(fields || {}).forEach(([key, field]) => {
    const path = FIELD_NAME_MAP[key];
    if (!path) return;
    form.batch(() => {
      const { isEmpty, isValid } = field;
      if (isEmpty) {
        form.change(path, 'empty');

        return;
      }

      form.change(path, undefined);
      if (path === 'appraisalPayment.creditCard.expiryDate') {
        form.change(path, isValid ? 'valid' : 'invalid');
      }

      if (!isEmpty) form.blur(path);
    });
  });
};

const handleCardFieldBlur = (event, form) => {
  const { emittedBy: key, fields } = event;
  const path = FIELD_NAME_MAP[key];
  const field = fields[key];
  if (!path || !field) return;
  form.change(path, field.isEmpty ? 'empty' : undefined);
  form.blur(path);
};

function mapBillingFields(data) {
  return {
    ...data,
    billingStreetAddress: data.address,
    billingCity: data.municipality,
    billingCountryCode: data.countryCode,
    billingPostalCode: data.postalCode,
    billingStateCode: data.stateCode,
  };
}

function Form({
  submitError,
  setSuccessTrue,
  token,
  submitting,
  isAllPropertiesMultiFamily,
}) {
  const form = useForm();
  const dispatch = useDispatch();
  const handleCreateOrder = useCallback(
    () => dispatch(createOrder(token)),
    [dispatch, token],
  );
  const loading = useSelector((state) => requestInProcess(state, 'APPRAISAL_PAYMENT'));

  const handleApprovePayment = useCallback(
    (data) => {
      const { appraisalPayment } = form.getState().values;
      const billingData = mapBillingFields(appraisalPayment);
      const payload = {
        ...billingData,
        paypal_order_id: data.orderID,
      };

      return dispatch(onApprove(token, payload, setSuccessTrue));
    },
    [dispatch, form, token],
  );

  const onErrorHandler = (error) => {
    let errorObj = error;
    const userFriendlyMessage = 'Payment submission failed';

    try {
      if (error instanceof Error) {
        const errorMessage = error.message;

        const jsonMatch = errorMessage.match(/\{.*\}/);
        if (jsonMatch) {
          const paypalErrorData = JSON.parse(jsonMatch[0]);
          const paypalErrorMessage = paypalErrorData.message || 'An error occurred during payment processing';
          const paypalErrorDetails = paypalErrorData.details || [];
          const allDetails = paypalErrorDetails.map(
            (detail) => `${detail.issue}: ${detail.description}`,
          ).join(', ');
          errorObj = paypalErrorMessage + (allDetails ? ` (${allDetails})` : '');
        } else {
          errorObj = userFriendlyMessage;
        }
      }
    } catch (parseError) {
      errorObj = userFriendlyMessage;
    }

    errorNotification(errorObj);
  };

  const initialOptions = {
    'client-id': payPalClientId,
    'enable-funding': 'venmo',
    'disable-funding': '',
    currency: 'USD',
    'data-page-type': 'product-details',
    components: 'buttons,card-fields',
    'data-sdk-integration-source': 'developer-studio',
  };

  return (
    <>
      {loading && <Loader />}
      <form>
        <PayPalScriptProvider options={initialOptions}>
          <PayPalCardFieldsProvider
            createOrder={handleCreateOrder}
            onApprove={handleApprovePayment}
            style={{
              body: {
                padding: 0,
              },
              input: {
                display: 'block',
                'box-sizing': 'border-box',
                height: '2.5em',
                'font-size': '1.2rem',
                'font-weight': '400',
                'line-height': '1.42857',
                color: '#1e1e1e',
                outline: '0',
                position: 'relative',
                'background-color': '#FFF',
                border: '1px solid #052959',
                'border-radius': '3px',
                'box-shadow': 'inset 0px 1px 2px 0px rgba(0,0,0,0.15)',
              },
              ':focus': {
                border: '1px solid #395905',
                outline: '0',
                'box-shadow': 'none',
              },
              '.invalid': {
                color: '#a6031f',
                border: '1px solid #a6031f',
                'box-shadow': 'none',
              },
              ':focus.invalid': {
                color: '#a6031f',
                border: '1px solid #a6031f',
                'box-shadow': 'none',
              },
            }}
            inputEvents={{
              onChange: (e) => handleCardFieldChange(e, form),
              onBlur: (e) => handleCardFieldBlur(e, form),
            }}
            onError={(error) => {
              onErrorHandler(error);
            }}
          >
            <StyledHeading>
              <FormFlex alignItems='center'>
                <FormBox width={[1, 1, 1 / 2]}>
                  <StyledLegend>Pay for Valuation</StyledLegend>
                </FormBox>
                <FormBox width={[1, 1, 1 / 2]}>
                  <Field
                    name='total'
                  >
                    {({ input }) => (
                      <Total>
                        <StyledTopTotal>
                          <p>TOTAL:</p>  <StyledMoney>${input.value}</StyledMoney>
                        </StyledTopTotal>
                      </Total>
                    )}
                  </Field>
                </FormBox>
              </FormFlex>
            </StyledHeading>
            {submitError && <SubmitError error={submitError} />}
            <Fields isAllPropertiesMultiFamily={isAllPropertiesMultiFamily} token={token} />
            <FooterFormFlex justifyContent='center' alignItems='center'>
              <FormBox>
                <Field name='total'>
                  {({ input }) => (
                    <SubmitPayment
                      input={input}
                      submitting={submitting}
                    />
                  )}
                </Field>
              </FormBox>
            </FooterFormFlex>
          </PayPalCardFieldsProvider>
        </PayPalScriptProvider>
      </form>
    </>
  );
}

const StyledHeading = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const Total = styled.div`
  display: flex;
  justify-content: end;
  @media (max-width: 768px) {
    justify-content: start;
  }
`;

const StyledLegend = styled(FormLegend)`
  margin-bottom: 1rem;
  font-size: var(--font-size-l);
  color: var(--color-primary);
  font-weight: 600;
`;

const StyledMoney = styled.span`
  font-weight: bold;
  color: var(--color-secondary);
  margin-left: 0.5rem;
`;

const StyledTopTotal = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30%;
  margin-bottom: 1rem;
  background-color: var(--color-icon-background);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--color-primary);

  @media (max-width: 600px) {
    width: 90%;
  }
`;

const FooterFormFlex = styled(FormFlex)`
  padding: 2rem;
`;

Form.propTypes = {
  submitError: PropTypes.string,
  setSuccessTrue: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  isAllPropertiesMultiFamily: PropTypes.bool,
};

Form.defaultProps = {
  isAllPropertiesMultiFamily: false,
  submitError: undefined,
};

export default Form;
